/**
 * Deployment logs panel
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import type { DeploymentLog } from '@/lib/deployment/types';

type Props = {
  logs: DeploymentLog[];
};

const getLogIcon = (level: DeploymentLog['level']) => {
  switch (level) {
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    case 'warn':
      return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    default:
      return <Info className="h-4 w-4 text-blue-600" />;
  }
};

const getLogColor = (level: DeploymentLog['level']) => {
  switch (level) {
    case 'success':
      return 'text-green-700 dark:text-green-300';
    case 'error':
      return 'text-red-700 dark:text-red-300';
    case 'warn':
      return 'text-yellow-700 dark:text-yellow-300';
    default:
      return 'text-foreground';
  }
};

export function DeploymentLogsPanel({ logs }: Props) {
  if (logs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deployment Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No logs yet. Start the deployment to see logs.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Deployment Logs</CardTitle>
          <Badge variant="outline">{logs.length} entries</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[200px]">
          <div className="space-y-2">
            {logs.map((log, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                {getLogIcon(log.level)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-muted-foreground">
                      {format(log.timestamp, 'HH:mm:ss')}
                    </span>
                    {log.step && (
                      <Badge variant="outline" className="text-xs capitalize">
                        {log.step}
                      </Badge>
                    )}
                  </div>
                  <p className={`text-sm ${getLogColor(log.level)}`}>
                    {log.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
